import { ResourceComponentContent } from '../../../types/elearning';
import { wordpressAPI } from '../../../services/wordpressApi';
import { useState, useEffect } from 'react';
import { WPMedia } from '../../../types/wordpress';
import { PaperClipIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Button from '../../ui/Button';

interface ResourceViewerProps {
  content: ResourceComponentContent;
}

export default function ResourceViewer({ content }: ResourceViewerProps) {
  const { mediaId, fileName, description, downloadText } = content;
  const [media, setMedia] = useState<WPMedia | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mediaId) {
      setLoading(true);
      wordpressAPI
        .fetchMedia(mediaId)
        .then((file) => {
          setMedia(file);
        })
        .catch((err) => {
          console.error('Failed to load resource:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [mediaId]);

  if (!media && !loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-500">Resource not found</p>
      </div>
    );
  }

  const handleDownload = () => {
    if (media?.source_url) {
      window.open(media.source_url, '_blank');
    }
  };

  const fileSize = media?.media_details?.filesize
    ? `${(media.media_details.filesize / 1024).toFixed(1)} KB`
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <PaperClipIcon className="h-6 w-6 text-gray-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">
            {fileName || media?.title?.rendered || 'Downloadable Resource'}
          </h4>
          {description && (
            <p className="text-sm text-gray-600 mb-2">{description}</p>
          )}
          {fileSize && (
            <p className="text-xs text-gray-500 mb-3">Size: {fileSize}</p>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleDownload}
            disabled={loading || !media}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {downloadText || 'Download'}
          </Button>
        </div>
      </div>
    </div>
  );
}
